import { useContext } from "react";
import styles from "./styles/PosSummary.module.css";
import DataContext from "../../../context/DataContext";
import {
    formatDateToLocalDate,
    formatTimeToLocalTime,
} from "../../../utils/utils";
import useCurrencySymbol from "../../../hooks/useCurrencySymbol";
import { useTranslation } from "react-i18next";
import POSDataContext from "../../../context/POSDataContext";
import AuthContext from "../../../context/AuthContext";
import OutputWindow from "../../../components/ui/output_window/OutputWindow";
import SummaryOutputDouble from "./SummaryOutputDouble";
import SummaryOutput from "./SummaryOutput";
import useNumberFormat from "../../../hooks/useNumberFormat";
/**
 * The POS summary panel
 */
const PosSummaryPanel = ({}) => {
    const { t } = useTranslation();
    const {
        connection, // {Object} - The client's settings
        currentCustom, // {Object} - The current client customizations
    } = useContext(DataContext);

    const {
        user, // {Object} - The authenticated user's data
    } = useContext(AuthContext);
    const {
        summaryPos, // {Object} - The summary data for POS
    } = useContext(POSDataContext);

    const currency = useCurrencySymbol(true);
    const currentSummary = summaryPos?.salesSummary;
    const formatNum = useNumberFormat();

    /**
     * The balance window content
     * @returns {JSX-Element} - The balance window contnent
     */
    const getBalanceWindowContent = () => {
        return (
            <div
                className={`${styles.window_content_container} v-flex-container`}
            >
                <div
                    className={`${styles.content_entry_container} flex-container`}
                >
                    <span className={`${styles.entry_label} flex-container`}>
                        {`${t("cash in")} :`}
                    </span>
                    <span className={`${styles.entry_value} flex-container`}>
                        {currency}
                        {formatNum(currentSummary?.totalDeposits)}
                    </span>
                </div>
                <div
                    className={`${styles.content_entry_container} flex-container`}
                >
                    <span className={`${styles.entry_label} flex-container`}>
                        {`${t("cash out")} :`}
                    </span>
                    <span className={`${styles.entry_value} flex-container`}>
                        {currency}
                        {formatNum(currentSummary?.totalWithdrawals)}
                    </span>
                </div>
                <div
                    className={`${styles.content_entry_container} flex-container`}
                >
                    <span className={`${styles.entry_label} flex-container`}>
                        {`${t("current balance")} :`}
                    </span>
                    <span className={`${styles.entry_value} flex-container`}>
                        {currency}
                        {formatNum(currentSummary?.totalBalance)}
                    </span>
                </div>
            </div>
        );
    };

    /**
     * The payments by type window content
     * @returns {JSX-Element} - The payments by type window contnent
     */
    const getTypeWindowContent = () => {
        return (
            <div
                className={`${styles.window_content_container} v-flex-container`}
            >
                <div
                    className={`${styles.content_entry_container} flex-container`}
                >
                    <span className={`${styles.entry_label} flex-container`}>
                        {`${t("cash")} :`}
                    </span>
                    <span className={`${styles.entry_value} flex-container`}>
                        {currency}
                        {formatNum(currentSummary?.netPaidByCash)}
                    </span>
                </div>
                <div
                    className={`${styles.content_entry_container} flex-container`}
                >
                    <span className={`${styles.entry_label} flex-container`}>
                        {`${t("credit card")} :`}
                    </span>
                    <span className={`${styles.entry_value} flex-container`}>
                        {currency}
                        {formatNum(currentSummary?.netPaidByCreditCard)}
                    </span>
                </div>
                <div
                    className={`${styles.content_entry_container} flex-container`}
                >
                    <span className={`${styles.entry_label} flex-container`}>
                        {`${t("cheque")} :`}
                    </span>
                    <span className={`${styles.entry_value} flex-container`}>
                        {currency}
                        {formatNum(currentSummary?.netPaidByCheque)}
                    </span>
                </div>
            </div>
        );
    };

    return (
        <aside className={`${styles.summary_container} v-flex-container`}>
            <section className={` ${styles.stretch} v-flex-container`}>
                <div
                    className={` ${styles.summary_top_container} v-flex-container`}
                >
                    <div
                        className={` ${styles.summary_double_container} flex-container`}
                    >
                        <div className={``}>
                            <SummaryOutputDouble
                                outputMainLabel={t("session info")}
                                label1={t("user ID")}
                                label2={t("user role")}
                                value1={user?.cashierID}
                                value2={user?.userRole}
                            />
                        </div>
                        <div className={``}>
                            <SummaryOutputDouble
                                outputMainLabel={t("session start")}
                                label1={t("date")}
                                label2={t("time")}
                                value1={
                                    user?.cashierSessionDate
                                        ? formatDateToLocalDate(
                                              user?.cashierSessionDate,
                                          )
                                        : "-"
                                }
                                value2={
                                    user?.cashierSessionDate
                                        ? formatTimeToLocalTime(
                                              user?.cashierSessionDate,
                                          )
                                        : "-"
                                }
                            />
                        </div>
                    </div>
                    <div
                        className={`${styles.windows_output_container}  flex-container`}
                    >
                        <div
                            className={` ${styles.main_window_output_container}  v-flex-container`}
                        >
                            <h3>{`${t("balance")} :`}</h3>
                            <OutputWindow
                                color={currentCustom?.color?.current}
                                content={getBalanceWindowContent()}
                            />
                        </div>
                        <div
                            className={` ${styles.main_window_output_container}  v-flex-container`}
                        >
                            <h3>{`${t("payments")} :`}</h3>
                            <OutputWindow
                                color={currentCustom?.color?.current}
                                content={getTypeWindowContent()}
                            />
                        </div>
                    </div>
                </div>
                <div
                    className={` ${styles.summary_divider}  flex-container`}
                ></div>

                <div
                    className={` ${styles.summary_bottom_container} flex-container`}
                >
                    <SummaryOutput
                        number={true}
                        outputLabel={t("instalments")}
                        value={`${currentSummary?.installmentsPaid || 0}`}
                    />
                    <SummaryOutput
                        number={true}
                        outputLabel={t("tickets")}
                        value={`${currentSummary?.ticketsPaid || 0}`}
                    />

                    <SummaryOutput
                        number={true}
                        outputLabel={t("permits")}
                        value={`${currentSummary?.permitsPaid || 0}`}
                    />
                    <SummaryOutput
                        number={true}
                        outputLabel={t("services")}
                        value={`${currentSummary?.servicesPaid || 0}`}
                    />
                </div>
            </section>
        </aside>
    );
};

export default PosSummaryPanel;
